import type { JSX, MouseEvent } from 'react';
import { styled } from 'styled-components';
import { forwardRef, useEffect, useState } from 'react';
import { isFunction } from '@wistia/type-guards';
import type { InputProps } from '../Input/Input';
import { Input } from '../Input/Input';
import { Icon } from '../Icon';
import { copyToClipboard } from '../../helpers/copyToClipboard';
import { IconButton } from '../IconButton';

const StyledIconButton = styled(IconButton)`
  /* override size for icon button since prop gets changed by Input */
  height: var(--icon-button-size-sm);
  width: var(--icon-button-size-sm);

  /* override input padding variable since it is used for Input Icon positioning */
  --wui-input-horizontal-padding: 4px;
`;

export type InputClickToCopyProps = Omit<
  InputProps,
  'autoSelect' | 'disabled' | 'rightIcon' | 'type' | 'value'
> & {
  /**
   * When disabled, the copy action will not be triggered
   */
  disabled?: boolean;

  /**
   * Callback function that is called when the value is copied to the clipboard
   */
  onCopy?: (value: string) => void;

  /**
   * the value of what is copied
   */
  value: string;
};

const COPY_SUCCESS_DURATION = 2000;

/**
 * Provides a readonly input that copies the text to the clipboard when clicked.
 */
export const InputClickToCopy = forwardRef<HTMLInputElement, InputClickToCopyProps>(
  ({ value, onCopy, disabled = false, ...props }, ref): JSX.Element => {
    const [isCopied, setIsCopied] = useState(false);

    useEffect(() => {
      if (isCopied) {
        const timeout = setTimeout(() => {
          setIsCopied(false);
        }, COPY_SUCCESS_DURATION);

        return () => {
          clearTimeout(timeout);
        };
      }

      return () => null;
    }, [isCopied]);

    const handleClick = (event: MouseEvent) => {
      event.preventDefault();

      if (disabled) {
        return;
      }

      if (isFunction(props.onClick)) {
        props.onClick(event);
      }

      void copyToClipboard(value).then(() => {
        setIsCopied(true);
        if (isFunction(onCopy)) {
          onCopy(value);
        }
        return value;
      });
    };

    return (
      <Input
        aria-label="Click to Copy"
        {...(props as unknown as InputProps)}
        ref={ref}
        disabled={disabled}
        readOnly={true}
        rightIcon={
          <StyledIconButton
            colorScheme={isCopied ? 'success' : 'inherit'}
            disabled={disabled}
            label="Copy to clipboard"
            onClick={handleClick}
            variant="ghost"
          >
            {isCopied ? <Icon type="checkmark-circle" /> : <Icon type="save-as-copy" />}
          </StyledIconButton>
        }
        value={value}
      />
    );
  },
);

InputClickToCopy.displayName = 'InputClickToCopy';
